import { useEffect, useState, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { useAuth } from "../context/AuthContext";
import { getSOSReportadosPendientes } from "./sosService";

export function useSOSSignalR() {
    const { session } = useAuth();
    const [hasPendingSOS, setHasPendingSOS] = useState(false);
    const [lastSOSPulse, setLastSOSPulse] = useState<number>(0);
    const [isConnected, setIsConnected] = useState(false);
    const hubConnectionRef = useRef<signalR.HubConnection | null>(null);

    const rolId = session?.rol?.id;
    const unidadResidencial = session?.unidad?.id;
    const isRolPorteroOrVigilante = rolId === "PT" || rolId === "VG";

    // Función para re-cargar el estado actual (si hay SOS o no)
    const checkPendingSOS = async () => {
        if (!unidadResidencial) return;
        try {
            const reportes = await getSOSReportadosPendientes(unidadResidencial);
            const pending = reportes.length > 0;
            setHasPendingSOS(pending);
        } catch (error) {
            console.error("Error al cargar estado SOS inicial:", error);
        }
    };

    useEffect(() => {
        // Solo inicia la conexión si es PT o VG
        if (!isRolPorteroOrVigilante || !unidadResidencial) {
            if (hubConnectionRef.current) {
                hubConnectionRef.current.stop();
                hubConnectionRef.current = null;
                setIsConnected(false);
            }
            return;
        }

        // Inicializar Hub Connection
        const connection = new signalR.HubConnectionBuilder()
            .withUrl("https://api.easyph.com.co/soshub")
            .withAutomaticReconnect()
            .build();

        hubConnectionRef.current = connection;

        // Métodos de evento manejados por SignalR
        connection.on("recibirNuevoSOS", (unidad: string, tipoUser: string, total_SOSs: number) => {
            console.log(`SignalR: recibirNuevoSOS - Unidad: ${unidad}, Tipo: ${tipoUser}, Total: ${total_SOSs}`);
            checkPendingSOS();
            setLastSOSPulse(Date.now());
        });

        connection.on("recibirCierreSOS", (unidad: string, tipoUser: string, total_SOSs: number) => {
            console.log(`SignalR: recibirCierreSOS - Unidad: ${unidad}, Tipo: ${tipoUser}, Total: ${total_SOSs}`);
            checkPendingSOS();
            setLastSOSPulse(Date.now());
        });

        // Iniciar conexión y unirse al grupo
        const startConnection = async () => {
            try {
                await connection.start();
                setIsConnected(true);
                console.log("SignalR Connected.");

                // Unirse al grupo de la Unidad Residencial y Tipo de Usuario
                await connection.invoke("UnirseAGrupo", unidadResidencial, rolId);

                // Se hace una validación inicial llamando a la API al conectarse
                await checkPendingSOS();
            } catch (err) {
                console.error("SignalR Connection Error: ", err);
                // Fallback: hacer la consulta inicial aunque no haya podido conectar SignalR a la primera
                checkPendingSOS();
            }
        };

        startConnection();

        return () => {
            if (connection.state !== signalR.HubConnectionState.Disconnected) {
                connection.stop();
            }
            setIsConnected(false);
        };
    }, [isRolPorteroOrVigilante, unidadResidencial, rolId]);

    return { hasPendingSOS, isConnected, lastSOSPulse };
}
